import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useSnackbar } from 'notistack';
import CodigoBarrasPreview from '../inventario/CodigoBarrasPreview';
import productoService from '../../services/productoService';
import companiaService, { type Compania } from '../../services/companiaService';
import { getApiErrorMessageAsync } from '../../utils/apiError';
import { persistGlobalIdCompania, readGlobalIdCompania } from '../../utils/companiaPreferida';

export interface EtiquetaLineaUi {
  codigoArticulo: string;
  descripcion: string;
  copias: number;
  incluido: boolean;
  codBarra: string;
  sinCodigo: boolean;
}

export interface EtiquetaDocumentoDetalle {
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidad?: number;
  tipoProveedor?: string;
}

function esLineaProducto(d: EtiquetaDocumentoDetalle): boolean {
  return (d.tipoProveedor ?? '').trim().toUpperCase() !== 'G' && !!(d.codigoArticulo || '').trim();
}

function copiasDesdeCantidad(cantidad?: number): number {
  const n = Math.round(Number(cantidad) || 0);
  return n > 0 ? n : 1;
}

function agruparDetalles(detalles: EtiquetaDocumentoDetalle[]): Array<{
  codigoArticulo: string;
  descripcion: string;
  copias: number;
}> {
  const map = new Map<string, { codigoArticulo: string; descripcion: string; copias: number }>();
  for (const d of detalles.filter(esLineaProducto)) {
    const codigo = (d.codigoArticulo || '').trim();
    const prev = map.get(codigo);
    const extra = copiasDesdeCantidad(d.cantidad);
    if (prev) {
      prev.copias += extra;
    } else {
      map.set(codigo, {
        codigoArticulo: codigo,
        descripcion: (d.descripcionArticulo || '').trim(),
        copias: extra,
      });
    }
  }
  return Array.from(map.values());
}

type EtiquetaItems = Array<{ codigoArticulo: string; copias: number }>;

export interface ImprimirEtiquetasCodigosDialogProps {
  open: boolean;
  /** 0 si no hay comprobante (p. ej. ficha de producto). */
  nroFormulario?: number;
  detalles: EtiquetaDocumentoDetalle[] | undefined;
  /** Texto para el cuerpo: "la factura" / "el remito". */
  textoOrigen?: string;
  fileName: string;
  /** Compañía del comprobante; tiene prioridad sobre la compañía global al abrir el diálogo. */
  idCompaniaDocumento?: string | null;
  obtenerPdf: (nroFormulario: number, items: EtiquetaItems, idCompania?: string) => Promise<Blob>;
  onClose: () => void;
  onPdfReady: (url: string, fileName: string) => void;
}

function idCompaniaInicial(companias: Compania[], idDocumento?: string | null): string {
  const ids = companias.map((c) => String(c.idCompania || '').trim()).filter(Boolean);
  const candidatos = [String(idDocumento ?? '').trim(), readGlobalIdCompania() ?? ''];
  return candidatos.find((id) => id && ids.includes(id)) ?? ids[0] ?? '';
}

const ImprimirEtiquetasCodigosDialog: React.FC<ImprimirEtiquetasCodigosDialogProps> = ({
  open,
  nroFormulario = 0,
  detalles,
  textoOrigen = 'el comprobante',
  fileName,
  idCompaniaDocumento,
  obtenerPdf,
  onClose,
  onPdfReady,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const [lineas, setLineas] = useState<EtiquetaLineaUi[]>([]);
  const [cargando, setCargando] = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [idCompania, setIdCompania] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    void companiaService
      .getCompanias({ pageNumber: 1, pageSize: 200 })
      .then((lista) => {
        if (cancelado) return;
        setCompanias(lista);
        setIdCompania(idCompaniaInicial(lista, idCompaniaDocumento));
      })
      .catch(() => {
        if (cancelado) return;
        setCompanias([]);
        setIdCompania(String(idCompaniaDocumento ?? '').trim() || readGlobalIdCompania() || '');
      });
    return () => {
      cancelado = true;
    };
  }, [open, idCompaniaDocumento]);

  const handleCompaniaChange = (id: string) => {
    setIdCompania(id);
    if (id) persistGlobalIdCompania(id);
  };

  const cargar = useCallback(async () => {
    const agrupadas = agruparDetalles(detalles || []);
    if (agrupadas.length === 0) {
      setLineas([]);
      return;
    }
    setCargando(true);
    try {
      const resueltas = await Promise.all(
        agrupadas.map(async (g) => {
          let codBarra = '';
          try {
            const prod = await productoService.getProducto(g.codigoArticulo);
            const raw =
              prod.codBarra
              ?? (prod as { CodBarra?: string | null }).CodBarra;
            codBarra = (raw || '').trim();
          } catch {
            /* sin ficha: se marca sin código */
          }
          return {
            ...g,
            incluido: !!codBarra,
            codBarra,
            sinCodigo: !codBarra,
          } satisfies EtiquetaLineaUi;
        }),
      );
      setLineas(resueltas);
    } finally {
      setCargando(false);
    }
  }, [detalles]);

  useEffect(() => {
    if (open) void cargar();
  }, [open, cargar]);

  const totalEtiquetas = useMemo(
    () => lineas.reduce((acc, l) => acc + (l.incluido && !l.sinCodigo ? l.copias : 0), 0),
    [lineas],
  );

  const actualizar = (codigoArticulo: string, patch: Partial<EtiquetaLineaUi>) => {
    setLineas((prev) => prev.map((l) => (l.codigoArticulo === codigoArticulo ? { ...l, ...patch } : l)));
  };

  const armarItems = (): EtiquetaItems | null => {
    const items: EtiquetaItems = [];
    for (const l of lineas) {
      if (l.incluido && !l.sinCodigo && l.copias > 0) {
        items.push({ codigoArticulo: l.codigoArticulo, copias: l.copias });
      }
    }
    if (items.length === 0) {
      enqueueSnackbar('No hay etiquetas para imprimir. Incluí al menos un producto con código.', {
        variant: 'warning',
      });
      return null;
    }
    return items;
  };

  const handleImprimirPdf = async () => {
    const items = armarItems();
    if (!items) return;
    try {
      setImprimiendo(true);
      const blob = await obtenerPdf(nroFormulario, items, idCompania || undefined);
      const url = URL.createObjectURL(blob);
      onPdfReady(url, fileName);
      onClose();
    } catch (error: unknown) {
      const msg = await getApiErrorMessageAsync(error, 'El servidor no pudo generar las etiquetas');
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setImprimiendo(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !imprimiendo && onClose()}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      aria-labelledby="dialog-etiquetas-titulo"
    >
      <DialogTitle id="dialog-etiquetas-titulo">¿Imprimir códigos de barras?</DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Se arma una etiqueta por unidad de {textoOrigen}. Podés ajustar copias o excluir productos.
        </Typography>
        {companias.length > 1 ? (
          <TextField
            select
            label="Compañía"
            value={idCompania}
            onChange={(e) => handleCompaniaChange(e.target.value)}
            disabled={imprimiendo}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            helperText="Nombre y logo que se imprimen en la etiqueta."
          >
            {companias.map((c) => {
              const id = String(c.idCompania || '').trim();
              return (
                <MenuItem key={id} value={id}>
                  {(c.nombre || '').trim() || id}
                </MenuItem>
              );
            })}
          </TextField>
        ) : null}
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : lineas.length === 0 ? (
          <Typography variant="body2">Este comprobante no tiene productos para etiquetar.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {lineas.map((l) => (
              <Box
                key={l.codigoArticulo}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 1.5,
                  opacity: l.incluido && !l.sinCodigo ? 1 : 0.72,
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Checkbox
                    checked={l.incluido && !l.sinCodigo}
                    disabled={l.sinCodigo || imprimiendo}
                    onChange={(e) => actualizar(l.codigoArticulo, { incluido: e.target.checked })}
                    inputProps={{ 'aria-label': `Incluir ${l.codigoArticulo}` }}
                    sx={{ mt: -0.5 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                      {l.descripcion || l.codigoArticulo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {l.codigoArticulo}
                    </Typography>
                    {l.sinCodigo ? (
                      <Typography variant="body2" color="warning.dark" sx={{ mt: 0.5 }}>
                        Sin código de barras: no se imprime esta línea.
                      </Typography>
                    ) : (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        sx={{ mt: 1 }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <CodigoBarrasPreview valor={l.codBarra} compact />
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                          <IconButton
                            aria-label={`Menos copias ${l.codigoArticulo}`}
                            onClick={() =>
                              actualizar(l.codigoArticulo, { copias: Math.max(1, l.copias - 1) })
                            }
                            disabled={imprimiendo || !l.incluido}
                            sx={{ border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 44, minHeight: 44 }}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <TextField
                            type="number"
                            value={l.copias}
                            onChange={(e) =>
                              actualizar(l.codigoArticulo, {
                                copias: Math.max(1, Math.min(9999, Number(e.target.value) || 1)),
                              })
                            }
                            disabled={imprimiendo || !l.incluido}
                            inputProps={{
                              min: 1,
                              max: 9999,
                              'aria-label': `Copias ${l.codigoArticulo}`,
                              style: { textAlign: 'center', width: 56 },
                            }}
                            size="small"
                          />
                          <IconButton
                            aria-label={`Más copias ${l.codigoArticulo}`}
                            onClick={() =>
                              actualizar(l.codigoArticulo, { copias: Math.min(9999, l.copias + 1) })
                            }
                            disabled={imprimiendo || !l.incluido}
                            sx={{ border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 44, minHeight: 44 }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          position: fullScreen ? 'sticky' : 'static',
          bottom: 0,
          bgcolor: 'background.paper',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Button onClick={onClose} disabled={imprimiendo} sx={{ textTransform: 'none', minHeight: 44 }}>
          Ahora no
        </Button>
        <Button
          variant="contained"
          onClick={handleImprimirPdf}
          disabled={imprimiendo || cargando || totalEtiquetas === 0}
          sx={{ textTransform: 'none', minHeight: 44 }}
        >
          {imprimiendo ? 'Generando…' : 'Imprimir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImprimirEtiquetasCodigosDialog;
